import { useState } from 'react';
import { getProductRepository } from '../lib/productRepository';
import {
  createPortableBackup,
  portableBackupFilename,
  serializePortableBackup,
  type ValidatedPortableBackup,
} from '../lib/portableBackup';
import { RestoreBackupControl } from './RestoreBackupControl';

export function DataStorageSettings({
  onRestoreComplete,
}: {
  onRestoreComplete(): void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const repositoryMode = getProductRepository().mode;

  const exportBackup = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const backup = await createPortableBackup();
      const blob = new Blob([serializePortableBackup(backup)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = portableBackupFilename(backup.createdAt);
        link.click();
      } finally {
        URL.revokeObjectURL(url);
      }
      setMessage('Backup created. Keep the downloaded file somewhere safe.');
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Backup could not be created.',
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRestoreComplete = (_backup: ValidatedPortableBackup): void => {
    onRestoreComplete();
  };

  return (
    <section className="card stack settings-grouped" aria-labelledby="data-storage-settings-title">
      <h2 id="data-storage-settings-title" className="mt-0">Data &amp; Storage</h2>
      <p className="page-lead mt-0">
        Your personal data is stored on this device. Export a portable copy for
        safekeeping or moving to another device.
      </p>
      {repositoryMode === 'server' ? (
        <p className="hint">
          Private server mode is active. Backup includes device-local data only,
          not SQLite, uploads, or spreadsheet records.
        </p>
      ) : null}
      <div className="row data-storage-actions">
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void exportBackup()}>
          {busy ? 'Exporting…' : 'Export Backup'}
        </button>
        <RestoreBackupControl disabled={busy} onRestoreComplete={handleRestoreComplete} />
      </div>
      <p className="hint mt-0">
        Backup files contain your personal Conscious Ritual data. Store them
        appropriately. Restore replaces this device&apos;s current data; it does
        not merge records.
      </p>
      {message ? <p role="status" className="notice">{message}</p> : null}
      {error ? <p role="alert" className="error-text">{error}</p> : null}
    </section>
  );
}
